"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessageReader = void 0;
const cdr_1 = require("@foxglove/cdr");
const DeserializationInfoCache_1 = require("./DeserializationInfoCache");
class MessageReader {
    constructor(rootDefinitionName, definitions) {
        const rootDefinition = definitions.find((def) => def.name === rootDefinitionName);
        if (rootDefinition == undefined) {
            throw new Error(`Root definition name "${rootDefinitionName}" not found in schema definitions.`);
        }
        // Build the deserialization info tree structure for the root definition.
        this.deserializationInfoCache = new DeserializationInfoCache_1.DeserializationInfoCache(definitions);
        this.rootDeserializationInfo =
            this.deserializationInfoCache.getComplexDeserializationInfo(rootDefinition);
    }
    // We template on R here for call site type information if the class type information T is not
    // known or available
    readMessage(buffer) {
        const reader = new cdr_1.CdrReader(buffer);
        const usesDelimiterHeader = reader.usesDelimiterHeader;
        const usesMemberHeader = reader.usesMemberHeader;
        return this.readAggregatedType(this.rootDeserializationInfo, reader, {
            usesDelimiterHeader,
            usesMemberHeader,
        });
    }
    readAggregatedType(deserInfo, reader, options, 
    /** The size of the struct if known (like from an emHeader). If it is known we do not read in a dHeader */
    knownTypeSize) {
        const readDelimiterHeader = options.usesDelimiterHeader && deserInfo.usesDelimiterHeader;
        const readMemberHeader = options.usesMemberHeader && deserInfo.usesMemberHeader;
        // Delimiter header is only read/written if the size of the type is not yet known
        // (If it hasn't already been read in from a surrounding emHeader)
        if (knownTypeSize == undefined && readDelimiterHeader) {
            reader.dHeader();
        }
        const msg = deserInfo.type === "struct"
            ? this.readStructType(deserInfo, reader, options)
            : this.readUnionType(deserInfo, reader, options);
        if (readMemberHeader) {
            reader.sentinelHeader();
        }
        return msg;
    }
    readStructType(deserInfo, reader, options) {
        const readMemberHeader = options.usesMemberHeader && deserInfo.usesMemberHeader;
        const msg = {};
        for (const field of deserInfo.fields) {
            msg[field.name] = this.readMemberFieldValue(field, reader, {
                readMemberHeader,
            }, options);
        }
        return msg;
    }
    readUnionType(deserInfo, reader, options) {
        const readMemberHeader = options.usesMemberHeader && deserInfo.usesMemberHeader;
        // looks like unions print an emHeader for the switchType
        if (readMemberHeader) {
            const { objectSize: objectSizeBytes } = reader.emHeader();
            if (objectSizeBytes !== deserInfo.switchTypeLength) {
                throw new Error(`Expected switchType length of ${deserInfo.switchTypeLength} but got ${objectSizeBytes} for ${deserInfo.definition.name ?? ""}`);
            }
        }
        const discriminatorValue = deserInfo.switchTypeDeser(reader);
        // get case for switchtype value based on matching predicate
        const caseDefType = getCaseForDiscriminator(deserInfo.definition, discriminatorValue);
        if (!caseDefType) {
            throw new Error(`No matching case found in ${deserInfo.definition.name ?? ""} for discriminator value ${discriminatorValue.toString()}`);
        }
        const fieldDeserInfo = this.deserializationInfoCache.buildFieldDeserInfo(caseDefType);
        return {
            [caseDefType.name]: this.readMemberFieldValue(fieldDeserInfo, reader, {
                readMemberHeader,
                parentName: deserInfo.definition.name,
            }, options),
        };
    }
    readMemberFieldValue(field, reader, emHeaderOptions, childOptions) {
        let emHeaderSizeBytes;
        if (emHeaderOptions.readMemberHeader) {
            const { id, objectSize: objectSizeBytes, lengthCode } = reader.emHeader();
            emHeaderSizeBytes = useEmHeaderAsLength(lengthCode) ? objectSizeBytes : undefined;
            // this can help spot misalignments in reading the data
            const definitionId = field.definitionId;
            if (definitionId != undefined && id !== definitionId) {
                throw Error(`CDR message deserializer error. Expected ${definitionId} but EMHEADER contained ${id} for field "${field.name}" in ${emHeaderOptions.parentName ?? "unknown"}`);
            }
        }
        if (field.typeDeserInfo.type === "struct" || field.typeDeserInfo.type === "union") {
            if (field.isArray === true) {
                // For dynamic length arrays we need to read a uint32 prefix
                const arrayLengths = field.arrayLengths ?? [reader.sequenceLength()];
                return this.readComplexNestedArray(reader, childOptions, field.typeDeserInfo, arrayLengths, 0);
            }
            else {
                return this.readAggregatedType(field.typeDeserInfo, reader, childOptions, emHeaderSizeBytes);
            }
        }
        else {
            const headerSpecifiedLength = emHeaderSizeBytes != undefined
                ? Math.floor(emHeaderSizeBytes / field.typeDeserInfo.typeLength)
                : undefined;
            if (field.typeDeserInfo.type === "array-primitive") {
                const deser = field.typeDeserInfo.deserialize;
                const arrayLengths = field.arrayLengths ??
                    // the byteLength written in the header doesn't help us determine count of strings in the array
                    // This will be the next field in the message
                    (field.type === "string"
                        ? [reader.sequenceLength()]
                        : [headerSpecifiedLength ?? reader.sequenceLength()]);
                if (arrayLengths.length > 1) {
                    const typedArrayDeserializer = () => {
                        return deser(reader, arrayLengths[arrayLengths.length - 1]);
                    };
                    // last arrayLengths length is handled in deserializer. It returns an array
                    return readNestedArray(typedArrayDeserializer, arrayLengths.slice(0, -1), 0);
                }
                else {
                    return deser(reader, arrayLengths[0]);
                }
            }
            else if (field.typeDeserInfo.type === "primitive") {
                return field.typeDeserInfo.deserialize(reader, headerSpecifiedLength);
            }
            else {
                throw new Error(`Unhandled deserialization info type`);
            }
        }
    }
    readComplexNestedArray(reader, options, deserInfo, arrayLengths, depth) {
        if (depth > arrayLengths.length - 1 || depth < 0) {
            throw Error(`Invalid depth ${depth} for array of length ${arrayLengths.length}`);
        }
        const array = [];
        for (let i = 0; i < arrayLengths[depth]; i++) {
            if (depth === arrayLengths.length - 1) {
                array.push(this.readAggregatedType(deserInfo, reader, options));
            }
            else {
                array.push(this.readComplexNestedArray(reader, options, deserInfo, arrayLengths, depth + 1));
            }
        }
        return array;
    }
}
exports.MessageReader = MessageReader;
function readNestedArray(deser, arrayLengths, depth) {
    if (depth > arrayLengths.length - 1 || depth < 0) {
        throw Error(`Invalid depth ${depth} for array of length ${arrayLengths.length}`);
    }
    const array = [];
    for (let i = 0; i < arrayLengths[depth]; i++) {
        if (depth === arrayLengths.length - 1) {
            array.push(deser());
        }
        else {
            array.push(readNestedArray(deser, arrayLengths, depth + 1));
        }
    }
    return array;
}
function getCaseForDiscriminator(unionDef, discriminatorValue) {
    for (const caseDef of unionDef.cases) {
        for (const predicate of caseDef.predicates) {
            if (predicate === discriminatorValue) {
                return caseDef.type;
            }
        }
    }
    return unionDef.defaultCase;
}
/** Only length Codes >= 5 should allow for emHeader sizes to be used in place of other integer lengths */
function useEmHeaderAsLength(lengthCode) {
    return lengthCode != undefined && lengthCode >= 5;
}
//# sourceMappingURL=MessageReader.js.map